// Typed API client for /v1/datasets/*.
//
// Backend (iter-5) returns the schema body directly — no `{data: ...}`
// envelope (unlike /auth/login). Confirmed against
// backend/app/schemas/datasets.py.

import { api } from '@/lib/api';
import type { AxiosProgressEvent } from 'axios';

export interface Dataset {
  id: string;
  name: string;
  n_rows: number;
  n_cols: number;
  column_names: string[];
  size_bytes: number;
  sha256: string;
  is_synthetic: boolean;
  created_at: string;
}

export interface DatasetListResponse {
  items: Dataset[];
  next_cursor: string | null;
}

export interface UploadDatasetOptions {
  name?: string;
  isSynthetic?: boolean;
  onUploadProgress?: (e: AxiosProgressEvent) => void;
  signal?: AbortSignal;
}

export interface UploadDatasetResult {
  dataset: Dataset;
  // false = backend deduped on (org_id, sha256) and returned an existing row
  // (HTTP 200); true = a brand-new row was created (HTTP 201). Used by the UI
  // to differentiate "Uploaded …" from "Already in your library".
  created: boolean;
}

export async function uploadDataset(
  file: File,
  options: UploadDatasetOptions = {},
): Promise<UploadDatasetResult> {
  const form = new FormData();
  form.append('file', file);
  if (options.name) form.append('name', options.name);
  if (options.isSynthetic !== undefined) {
    form.append('is_synthetic', String(options.isSynthetic));
  }
  const resp = await api.post<Dataset>('/datasets', form, {
    // Let axios set the multipart boundary itself.
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: options.onUploadProgress,
    signal: options.signal,
    // CSV parsing on a 5MB file can take a beat under load; bump from
    // the default 30s only for this call.
    timeout: 60_000,
  });
  return { dataset: resp.data, created: resp.status === 201 };
}

export async function getDataset(id: string): Promise<Dataset> {
  const resp = await api.get<Dataset>(`/datasets/${id}`);
  return resp.data;
}

export interface ListDatasetsParams {
  cursor?: string;
  limit?: number;
}

export async function listDatasets(params: ListDatasetsParams = {}): Promise<DatasetListResponse> {
  const resp = await api.get<DatasetListResponse>('/datasets', { params });
  return resp.data;
}

export async function deleteDataset(id: string): Promise<void> {
  await api.delete(`/datasets/${id}`);
}
